<template>
  <v-form ref="form" lazy-validation>
    <v-card class="p-3">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-row align="end">
          <template v-for="field in fields">

            <!-- Tag Category narrows the Tags options. Not a criterion: it is
                 never sent, it only shrinks the dropdown. -->
            <v-col v-if="field.categoryFilter" :key="`${field.key}-cat`" cols="4">
              <v-autocomplete v-model="selectedCategory"
                              :items="allCats"
                              class="mt-4" item-text="name" item-value="id"
                              label="Tag Category"/>
            </v-col>

            <v-col :key="field.key" :cols="field.cols" :data-filter-field="field.key">

              <date-picker v-if="field.type === 'date'"
                           v-model="values[field.key]"
                           :initial-value="values[field.key]"
                           :label="field.label"/>

              <v-checkbox v-else-if="field.type === 'checkbox'"
                          v-model="values[field.key]"
                          :disabled="isDisabled(field)"
                          :label="field.label"/>

              <v-text-field v-else-if="field.type === 'text'"
                            v-model="values[field.key]"
                            :hint="field.hint"
                            :label="field.label"
                            clearable
                            persistent-hint/>

              <v-select v-else-if="field.type === 'select'"
                        v-model="values[field.key]"
                        :items="$props[field.choicesProp]"
                        :label="field.label"
                        :loading="$store.state.loading_list.includes(field.loadingKey)"
                        clearable/>

              <v-autocomplete v-else-if="field.type === 'country'"
                              v-model="values[field.key]"
                              :items="countries"
                              item-text="label" item-value="value"
                              :label="field.label"
                              clearable/>

              <v-autocomplete v-else-if="field.type === 'multi'"
                              v-model="values[field.key]"
                              :filter="(item, query) => matches(field, item, query)"
                              :item-text="field.itemText"
                              :items="optionsFor(field)"
                              :label="field.label"
                              chips class="mt-4" multiple return-object small-chips
                              @change="tagSearchInput = ''">
                <template v-slot:selection="data">
                  <v-chip v-bind="data.attrs"
                          :input-value="data.selected"
                          close
                          @click="data.select"
                          @click:close="removeSelected(field, data.item)">
                    <div style="overflow: hidden">{{ field.itemTitle(data.item) }}</div>
                  </v-chip>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title v-html="field.itemTitle(data.item)"></v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="error" text @click="cancelClicked()">
          Cancel
        </v-btn>
        <v-btn color="success" text @click="okClicked()">
          Ok
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
/**
 * The filter form, for every surface. [IT-707 P2.2]
 *
 * Replaces PhotoFilterModal / QCPhotoFilterModal / GroupQCFilterModal, which
 * were the same form three times with different field subsets, labels and
 * column widths. The subset is now derived from the surface's capabilities, so
 * a page cannot offer a control its backend throws away — the defect class
 * IT-796 and the group-tagging checkboxes both belong to.
 *
 * `pageLevel` lets a view keep a criterion in its own state and out of here, so
 * GroupCountQC's review toggles stay single-bound rather than existing twice.
 */
import DatePicker from './DatePicker.vue'
import {mapGetters, mapState} from 'vuex'
import {COUNTRIES, defaultsFor, fieldsFor} from './fields'
import {SURFACES} from './capabilities'

const TITLES = {
  [SURFACES.PHOTO_TAGGING]: 'Filter',
  [SURFACES.PHOTO_QC]: 'Filter Tagged Photos',
  [SURFACES.GROUP_TAGGING]: 'Filter',
  [SURFACES.GROUP_QC]: 'Filter Tagged Groups',
}

export default {
  name: 'FilterModal',
  components: {
    DatePicker,
  },
  props: {
    surface: {
      type: String,
      required: true,
    },
    photoFilters: {
      type: Object,
      default: () => ({}),
    },
    retailerChoices: {
      type: Array,
      default: () => [],
    },
    departmentChoices: {
      type: Array,
      default: () => [],
    },
    pageLevel: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      values: defaultsFor(this.surface, this.pageLevel),
      selectedCategory: 0,
      tagSearchInput: null,
      countries: COUNTRIES,
    }
  },
  mounted() {
    // Seed from the bar's current state, falling back to the field default. A
    // key the bar does not carry (because its surface cannot apply it) must not
    // land here as undefined — that is how includePartiallyTagged silently
    // became false and narrowed the queue.
    this.fields.forEach(({key}) => {
      const incoming = this.photoFilters[key]
      if (incoming !== undefined && incoming !== null) this.values[key] = incoming
    })

    this.$emit('modal-mounted', true)
  },
  computed: {
    ...mapState({
      // ALL tags, deliberately — not the `visibleTags` getter the tagger views
      // read. IT-704 hides derived (machine-owned) categories from the tagger
      // PICKER on enabled tenants; picking and filtering are different acts,
      // and filtering by a derived Brand is useful precisely because you
      // cannot hand-apply it — it is how you check what the derivation did.
      // Aligning this with the views would silently remove the only way to
      // search a derived value on the tenants where derivation does the work.
      // Guarded by filter-unified.spec.js.
      tags: (state) => state.tags.tags,
      categories: (state) => state.categories.categories,
      users: (state) => state.users.users,
    }),
    ...mapGetters(['getProjects', 'getJobs']),
    fields() {
      return fieldsFor(this.surface, this.pageLevel)
    },
    title() {
      return TITLES[this.surface] || 'Filter'
    },
    allCats() {
      const cats = (this.categories || []).map((cat) => ({id: cat.id, name: cat.name}))
      cats.push({id: 0, name: 'All'})
      return cats
    },
    filteredTags() {
      const categoryId = parseInt(this.selectedCategory)
      if (!categoryId) return this.tags || []
      return (this.tags || []).filter((tag) => tag.category && tag.category.id === categoryId)
    },
  },
  methods: {
    optionsFor(field) {
      return {
        tags: this.filteredTags,
        users: this.users || [],
        projects: this.getProjects || [],
        jobs: this.getJobs || [],
      }[field.source] || []
    },
    /** One substring match for every multi-select, on its own display label. */
    matches(field, item, queryText) {
      const search = String(queryText || '').toLowerCase()
      return String(field.itemTitle(item)).toLowerCase().indexOf(search) > -1
    },
    removeSelected(field, item) {
      const selected = this.values[field.key] || []
      const index = selected.indexOf(item)
      if (index >= 0) selected.splice(index, 1)
    },
    isDisabled(field) {
      return typeof field.disabledWhen === 'function' ? field.disabledWhen(this.values) : false
    },
    cancelClicked() {
      this.$emit('dialog-clicked')
    },
    okClicked() {
      // Exactly the rendered keys — no more (a key the surface ignores) and no
      // fewer (a control collected and then dropped, which is IT-703).
      const selected = {}
      this.fields.forEach(({key}) => {
        selected[key] = this.values[key]
      })

      this.$emit('apply-filters', selected)
      this.$emit('dialog-clicked')
    },
  },
}
</script>

<style>
</style>
